import { Injectable } from '@angular/core';
import {
    FlyFreelyError,
    FlyFreelyLoggingService,
    GqlMissionCriteria,
    isApprovalApplicable,
    MissionService,
    MissionSortField,
    MissionSummary,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Injectable()
export class LinkTestFlightDataService {
    private workingSource = new ReplaySubject<boolean>(1);
    private missionsSource = new ReplaySubject<MissionSummary[]>(1);
    private currentPageSource = new BehaviorSubject<number>(0);
    private totalItemsSource = new ReplaySubject<number>(1);
    private hasMissionsSource = new ReplaySubject<boolean>(1);

    working$ = this.workingSource.asObservable();
    missions$ = this.missionsSource.asObservable();
    currentPage$ = this.currentPageSource.asObservable();
    totalItems$ = this.totalItemsSource.asObservable();
    hasMissions$ = this.hasMissionsSource.asObservable();

    private workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private missionService: MissionService,
        private logging: FlyFreelyLoggingService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => this.workingSource.next(working));
    }

    ngOnDestroy() {
        this.workingSource.complete();
        this.missionsSource.complete();
        this.currentPageSource.complete();
        this.totalItemsSource.complete();
        this.hasMissionsSource.complete();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    findMissions(
        page: number,
        limit: number,
        sortFields: MissionSortField[],
        criteria: GqlMissionCriteria
    ) {
        // doneWorking is used for GraphQL to prevent the working state ending early.
        const doneWorking = this.workTracker.createTracker();

        this.missionService
            .findMissionSummaries(criteria, {
                page: page,
                pageSize: limit,
                sortFields
            })
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: missions => {
                    const missionDetails = missions.results.map(r => ({
                        ...r,
                        locationName: r.location?.name,
                        organisationName: r.organisation?.name,
                        missionCrewDetails: r.missionCrew,
                        craftNicknames: r.rpaLinkList.map(c => c.rpa.nickname),
                        missionType: r.missionOperationType,
                        missionWorkflowVersion: r.workflowVersion?.workflow,
                        missionApproval: r.missionApprovals
                            .filter(a => isApprovalApplicable(a.status))
                            .slice(-1)[0],
                        flightConformanceResultList:
                            r.flightConformanceResultList
                    }));
                    this.totalItemsSource.next(missions.count);
                    this.currentPageSource.next(page);
                    this.missionsSource.next(missionDetails);
                    if (missions.count > 0) {
                        this.hasMissionsSource.next(true);
                    }
                    doneWorking();
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(error);
                    this.missionsSource.next([]);
                    this.totalItemsSource.next(0);
                    this.currentPageSource.next(0);
                    doneWorking();
                }
            });
    }
}
